import { useState } from 'react';
import { format, parseISO, subDays } from 'date-fns';
import { toast } from 'sonner';
import { useApp } from '@/contexts/AppContext';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { normalizeHealthSubjectRecord } from '../../../ui-foundation/features/health';
import {
  AppBottomSheet,
  AppBottomSheetHeader,
  AppButtonRow,
  AppCardBody,
  AppFieldGroup,
  AppInput,
  AppNativeSelect,
  AppText,
  AppTextArea,
} from '../../../ui-foundation';
import type { HealthSubjectOption } from './health-shared';
import AppDateField from '@/components/AppDateField';

type HealthMedicationRow = Database['public']['Tables']['health_medications']['Row'];

interface Props {
  onClose: () => void;
  onSaved: () => void;
  editMedication?: HealthMedicationRow | null;
  subjectOptions: HealthSubjectOption[];
  initialSubjectValue?: string;
}

function resolveSubjectValue(
  subjectOptions: HealthSubjectOption[],
  editMedication?: HealthMedicationRow | null,
  initialSubjectValue?: string,
) {
  if (initialSubjectValue && subjectOptions.some((option) => option.value === initialSubjectValue)) {
    return initialSubjectValue;
  }

  const normalized = normalizeHealthSubjectRecord({
    subjectType: editMedication?.subject_type,
    subjectId: editMedication?.subject_id,
    subjectName: editMedication?.subject_name || editMedication?.person_name,
  });

  const match = subjectOptions.find((option) =>
    option.subjectType === normalized.subjectType && option.subjectId === normalized.subjectId
  );

  return match?.value || subjectOptions[0]?.value || 'self';
}

export default function HealthMedicationForm({
  onClose,
  onSaved,
  editMedication,
  subjectOptions,
  initialSubjectValue,
}: Props) {
  const { user } = useApp();
  const isEdit = Boolean(editMedication);

  const [subjectValue, setSubjectValue] = useState(() => resolveSubjectValue(subjectOptions, editMedication, initialSubjectValue));
  const [medicationName, setMedicationName] = useState(editMedication?.medication_name || '');
  const [dosage, setDosage] = useState(editMedication?.dosage || '');
  const [frequency, setFrequency] = useState(editMedication?.frequency || '');
  const [repeatDueDate, setRepeatDueDate] = useState<Date | undefined>(
    editMedication?.repeat_due_date ? parseISO(editMedication.repeat_due_date) : undefined
  );
  const [notes, setNotes] = useState(editMedication?.notes || '');
  const [saving, setSaving] = useState(false);

  const selectedSubject = subjectOptions.find((option) => option.value === subjectValue) || subjectOptions[0];
  const canSave = Boolean(selectedSubject) && medicationName.trim().length > 0;

  const syncRepeatReminder = async (medication: HealthMedicationRow, subject: HealthSubjectOption) => {
    if (medication.reminder_id) {
      await supabase.from('health_reminders').delete().eq('id', medication.reminder_id);
    }

    if (!medication.repeat_due_date) {
      if (medication.reminder_id) {
        await supabase.from('health_medications').update({ reminder_id: null }).eq('id', medication.id);
      }
      return;
    }

    const reminderDate = format(subDays(parseISO(medication.repeat_due_date), 7), 'yyyy-MM-dd');
    const reminderNotes = [
      `For ${subject.subjectName}`,
      medication.dosage,
      medication.frequency,
      medication.notes,
    ].filter(Boolean).join(' · ');

    const { data: reminderRow, error: reminderError } = await supabase
      .from('health_reminders')
      .insert({
        user_id: medication.user_id,
        type: 'medication',
        title: `Request repeat: ${medication.medication_name}`,
        due_date: reminderDate,
        due_time: null,
        frequency_months: null,
        notes: reminderNotes || null,
        reminder_days_before: 7,
        subject_type: subject.subjectType,
        subject_id: subject.subjectId,
        subject_name: subject.subjectName,
        recurrence_type: 'one-off',
        recurrence_interval: null,
        recurrence_unit: null,
        recurrence_day_of_week: null,
        recurrence_week_of_month: null,
        recurrence_month_day: null,
      } as any)
      .select('id')
      .single();

    if (reminderError) {
      throw reminderError;
    }

    await supabase.from('health_medications').update({ reminder_id: reminderRow.id }).eq('id', medication.id);
  };

  const handleSave = async () => {
    if (!user || !canSave || !selectedSubject) return;

    setSaving(true);

    const payload = {
      user_id: user.id,
      person_name: selectedSubject.subjectName,
      medication_name: medicationName.trim(),
      dosage: dosage.trim() || null,
      frequency: frequency.trim() || null,
      repeat_due_date: repeatDueDate ? format(repeatDueDate, 'yyyy-MM-dd') : null,
      notes: notes.trim() || null,
      subject_type: selectedSubject.subjectType,
      subject_id: selectedSubject.subjectId,
      subject_name: selectedSubject.subjectName,
    } as const;

    const { data: medicationRow, error } = isEdit && editMedication
      ? await supabase
          .from('health_medications')
          .update(payload)
          .eq('id', editMedication.id)
          .select('*')
          .single()
      : await supabase
          .from('health_medications')
          .insert(payload)
          .select('*')
          .single();

    if (error || !medicationRow) {
      console.error(error);
      toast.error('Could not save the medication tracker right now.');
      setSaving(false);
      return;
    }

    try {
      await syncRepeatReminder(medicationRow as HealthMedicationRow, selectedSubject);
      setSaving(false);
      onSaved();
    } catch (syncError) {
      console.error(syncError);
      toast.error('Medication saved, but the repeat reminder could not be synced.');
      setSaving(false);
      onSaved();
    }
  };

  return (
    <AppBottomSheet onClose={onClose}>
      <AppBottomSheetHeader
        title={isEdit ? 'Edit medication' : 'Add medication'}
        subtitle="Track who it is for, what they take, and when to request the next repeat."
      />

      <AppCardBody className="flex-1 overflow-y-auto px-5 py-5" gap="$lg">
        <AppFieldGroup label="Who is this for?">
          <AppNativeSelect value={subjectValue} onChange={(event) => setSubjectValue(event.target.value)}>
            {subjectOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </AppNativeSelect>
        </AppFieldGroup>

        <AppFieldGroup label="Medication">
          <AppInput
            tag="input"
            value={medicationName}
            onChange={(event) => setMedicationName((event.target as HTMLInputElement).value)}
            placeholder="e.g. Sertraline 50mg"
          />
        </AppFieldGroup>

        <div className="grid gap-4 sm:grid-cols-2">
          <AppFieldGroup label="Dosage">
            <AppInput
              tag="input"
              value={dosage}
              onChange={(event) => setDosage((event.target as HTMLInputElement).value)}
              placeholder="e.g. 1 tablet"
            />
          </AppFieldGroup>

          <AppFieldGroup label="Frequency">
            <AppInput
              tag="input"
              value={frequency}
              onChange={(event) => setFrequency((event.target as HTMLInputElement).value)}
              placeholder="e.g. Twice daily"
            />
          </AppFieldGroup>
        </div>

        <AppDateField
          label="Repeat prescription due date"
          value={repeatDueDate ? format(repeatDueDate, 'yyyy-MM-dd') : ''}
          onChange={(nextValue) => setRepeatDueDate(nextValue ? parseISO(nextValue) : undefined)}
          helper="Adulting will create a repeat-request reminder 7 days before this date."
        />

        <AppFieldGroup label="Notes (optional)">
          <AppTextArea
            value={notes}
            onChange={(event) => setNotes((event.target as HTMLTextAreaElement).value)}
            placeholder="Pharmacy, side effects, or anything else worth keeping handy"
            rows={3}
          />
        </AppFieldGroup>

        <AppButtonRow justifyContent="flex-end">
          <AppButton tone="outline" onPress={onClose}>
            Cancel
          </AppButton>
          <AppButton onPress={handleSave} disabled={!canSave || saving}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Save medication'}
          </AppButton>
        </AppButtonRow>
      </AppCardBody>
    </AppBottomSheet>
  );
}
